import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SearchableSelect } from "@/components/SearchableSelect";
import { APPLICATION_STATUS_LABELS, APPLICATION_STATUS_TONES, StatusPill } from "@/components/StatusPill";
import { Notice } from "@/components/Notice";
import { Loading } from "@/components/Loading";
import { useConfirm } from "@/components/ConfirmDialog";
import { formatMoney } from "@/utils/quoteTotals";
import { formatDate } from "@/utils/dates";
import { toastApiError } from "@/lib/toast";
import { cn } from "@/lib/utils";
import {
  equipment,
  EQUIPMENT_FULFILLMENT_STATUSES,
  EQUIPMENT_STATUS_LABELS,
  EQUIPMENT_STATUS_TONES,
  type EquipmentCatalog,
  type EquipmentLineInput,
  type EquipmentNeed,
  type EquipmentOrder,
  type EquipmentOrderSave,
  type EquipmentOrderStatus,
  type EquipmentPaymentOption,
  type EquipmentPricedLine,
  type EquipmentShipTo,
} from "@/features/equipment/api/equipment.api";
import { CoverBox, MultiPills, Pills, QtyStepper, SumRow } from "@/features/equipment/components/OrderPieces";

const MAX_QTY = 99;
const SAVE_DELAY_MS = 700;

const PACKAGES: { id: string; name: string; hint: string; lines: [string, number][] }[] = [
  { id: "counter", name: "Counter starter", hint: "1 POS bundle, kitchen printer, install day", lines: [["pos-156-bundle", 1], ["kitchen-printer", 1], ["implementation-day", 1]] },
  { id: "full", name: "Full-service", hint: "2 POS bundles, kitchen display, 2 handhelds, install + menu", lines: [["pos-156-bundle", 2], ["kds-21", 1], ["pax-a920-pro", 2], ["implementation-day", 1], ["menu-programming", 1]] },
  { id: "retail", name: "Retail", hint: "1 POS bundle, barcode scanner, label printer, install day", lines: [["pos-156-bundle", 1], ["barcode-scanner", 1], ["label-printer", 1], ["implementation-day", 1]] },
  { id: "terminal", name: "Terminal only", hint: "1 PAX A35 terminal", lines: [["pax-a35", 1]] },
];

const NEXT_STATUS_LABELS: Partial<Record<EquipmentOrderStatus, string>> = {
  Draft: "Reopen for the agent to edit",
  Submitted: "Approve",
};

const NEED_ORDER: EquipmentNeed[] = ["NewEquipment", "OwnHardware", "VirtualTerminal"];
const TERMINAL_CODE = "virtual-terminal";
const TERMINAL_MONTHLY_CODE = "virtual-terminal-monthly";

function primaryNeed(needs: EquipmentNeed[]): EquipmentNeed {
  return NEED_ORDER.find((n) => needs.includes(n)) ?? "NoneNeeded";
}

interface Draft {
  need: EquipmentNeed;
  needs: EquipmentNeed[];
  payment: EquipmentPaymentOption;
  shipTo: EquipmentShipTo;
  shipAddress: string;
  paybackMonths: number | null;
  bundleCode: string;
  items: EquipmentLineInput[];
  covers: Record<string, { percent: boolean; value: number }>;
  notes: string;
}

const COVER_ONLY_KINDS = new Set(["License", "Bundle", "Service"]);

function toDraft(o: EquipmentOrder): Draft {
  const covers: Draft["covers"] = {};
  for (const l of o.lines) {
    if (l.code && COVER_ONLY_KINDS.has(l.kind)) covers[l.code] = { percent: l.coverPercent, value: l.coverValue };
  }
  const needs: EquipmentNeed[] = [o.need];
  if (o.need !== "OwnHardware" && o.lines.some((l) => l.kind === "OwnDevice")) needs.push("OwnHardware");
  if (o.need !== "VirtualTerminal" && o.lines.some((l) => l.code === TERMINAL_MONTHLY_CODE)) needs.push("VirtualTerminal");
  return {
    need: o.need,
    needs,
    payment: o.payment,
    shipTo: o.shipTo,
    shipAddress: o.shipAddress ?? "",
    paybackMonths: o.paybackMonths,
    bundleCode: o.bundleCode ?? "",
    items: o.lines
      .filter((l) => !COVER_ONLY_KINDS.has(l.kind))
      .map((l) => ({
        code: l.kind === "OwnDevice" ? null : l.code,
        deviceName: l.kind === "OwnDevice" ? l.name : null,
        qty: l.qty,
        coverPercent: l.coverPercent,
        coverValue: l.coverValue,
      })),
    covers,
    notes: o.notes ?? "",
  };
}

function toSave(d: Draft): EquipmentOrderSave {
  const coverLines: EquipmentLineInput[] = Object.entries(d.covers)
    .filter(([, c]) => c.value > 0)
    .map(([code, c]) => ({ code, deviceName: null, qty: 1, coverPercent: c.percent, coverValue: c.value }));
  return {
    need: d.need,
    payment: d.payment,
    shipTo: d.shipTo,
    shipAddress: d.shipAddress.trim() || null,
    paybackMonths: d.paybackMonths,
    bundleCode: d.bundleCode || null,
    lines: [
      ...d.items.filter((i) => i.code || i.deviceName),
      ...coverLines,
      ...(d.need !== "VirtualTerminal" && d.needs.includes("VirtualTerminal")
        ? [{ code: TERMINAL_CODE, deviceName: null, qty: 1, coverPercent: false, coverValue: 0 }]
        : []),
    ],
    notes: d.notes.trim() || null,
  };
}

export function EquipmentStartPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const applicationId = params.get("applicationId");
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!applicationId) {
      navigate("/equipment", { replace: true });
      return;
    }
    equipment
      .start(applicationId)
      .then((o) => navigate(`/equipment/orders/${o.id}`, { replace: true }))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Couldn't start the order."));
  }, [applicationId, navigate]);

  return error ? (
    <div className="space-y-3">
      <Notice tone="error">{error}</Notice>
      <Link to="/equipment" className="text-sm text-muted-foreground hover:underline">← Order Equipment</Link>
    </div>
  ) : (
    <Loading>Opening the order…</Loading>
  );
}

export function EquipmentOrderPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [order, setOrder] = useState<EquipmentOrder | null>(null);
  const [catalog, setCatalog] = useState<EquipmentCatalog | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const dirty = useRef(false);
  const [buildOwn, setBuildOwn] = useState(false);
  const saveSeq = useRef(0);

  useEffect(() => {
    let cancelled = false;
    Promise.all([equipment.get(id), equipment.catalog()])
      .then(([o, c]) => {
        if (cancelled) return;
        setOrder(o);
        setCatalog(c);
        setDraft(toDraft(o));
      })
      .catch((e: unknown) => !cancelled && setError(e instanceof Error ? e.message : "Couldn't load the order."));
    return () => {
      cancelled = true;
    };
  }, [id]);

  const saveNow = useCallback(
    async (d: Draft) => {
      const seq = ++saveSeq.current;
      setSaving(true);
      try {
        const saved = await equipment.save(id, toSave(d));
        if (seq === saveSeq.current) {
          setOrder(saved);
          setSaveError(null);
        }
        return saved;
      } catch (e) {
        if (seq === saveSeq.current) setSaveError(e instanceof Error ? e.message : "Couldn't save.");
        return null;
      } finally {
        if (seq === saveSeq.current) setSaving(false);
      }
    },
    [id],
  );

  useEffect(() => {
    if (!draft || !dirty.current) return;
    const t = window.setTimeout(() => void saveNow(draft), SAVE_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [draft, saveNow]);

  function update(patch: Partial<Draft>) {
    dirty.current = true;
    setDraft((d) => (d ? { ...d, ...patch } : d));
  }

  const byCode = useMemo(() => new Map((catalog?.items ?? []).map((i) => [i.code, i])), [catalog]);
  const pricedByCode = useMemo(() => {
    const m = new Map<string, EquipmentPricedLine>();
    for (const l of order?.lines ?? []) if (l.code) m.set(l.code, l);
    return m;
  }, [order]);

  if (error) return <Notice tone="error">{error}</Notice>;
  if (!order || !catalog || !draft) return <Loading>Loading…</Loading>;

  const backOffice = order.viewerIsBackOffice;
  const editable = order.canEdit;
  const t = order.totals;
  const bundles = catalog.items.filter((i) => i.kind === "Bundle");
  const addable = catalog.items.filter((i) =>
    draft.need === "NewEquipment" ? ["Hardware", "Setup", "Module"].includes(i.kind) : i.kind === "Module",
  );
  const autoLines = order.lines.filter((l) => COVER_ONLY_KINDS.has(l.kind));
  const visibleItems = draft.items
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => (draft.needs.includes("OwnHardware") ? true : item.code !== null));
  const isMonthly = (item: EquipmentLineInput) =>
    item.code !== null && (byCode.get(item.code)?.monthly ?? pricedByCode.get(item.code)?.monthly ?? false);
  const pickedHardware = draft.items.filter((i) => i.code && byCode.get(i.code)?.kind !== "Module");
  const matchedPackage = PACKAGES.find((p) => {
    const lines = p.lines.filter(([code]) => byCode.has(code));
    return lines.length === pickedHardware.length && lines.every(([code, qty]) => pickedHardware.some((i) => i.code === code && i.qty === qty));
  });
  const activePackage = matchedPackage?.id ?? (buildOwn || pickedHardware.length > 0 ? "own" : null);
  const oneTimeItems = visibleItems.filter(({ item }) => !isMonthly(item));
  const monthlyItems = visibleItems.filter(({ item }) => isMonthly(item));
  const oneTimeAuto = autoLines.filter((l) => !l.monthly);
  const monthlyAuto = autoLines.filter((l) => l.monthly);

  function sectionRow(title: string) {
    return (
      <TableRow key={title} className="bg-slate-50 hover:bg-slate-50">
        <TableCell colSpan={editable ? 5 : 4} className="py-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {title}
        </TableCell>
      </TableRow>
    );
  }

  function coveredNote(l: EquipmentPricedLine) {
    return l.agentCovers > 0 ? (
      <div className="text-xs text-emerald-700">
        you cover {formatMoney(l.agentCovers)}
        {l.monthly ? "/mo" : ""}
      </div>
    ) : null;
  }

  function itemRow({ item, index }: { item: EquipmentLineInput; index: number }) {
    const cat = item.code ? byCode.get(item.code) : undefined;
    const priced = item.code ? pricedByCode.get(item.code) : undefined;
    const own = item.code === null;
    const maxQty = cat?.maxQty ?? (cat?.kind === "Module" ? 1 : MAX_QTY);
    const lockedCover = cat?.kind === "Hardware" && draft!.payment !== "Upfront";
    return (
      <TableRow key={`${item.code ?? "own"}-${index}`}>
        <TableCell>
          {own ? (
            editable ? (
              <SearchableSelect
                value={item.deviceName ?? ""}
                onValueChange={(v) => setItem(index, { deviceName: v })}
                options={catalog!.ownDeviceModels.map((m) => ({ value: m, label: m }))}
                placeholder="Pick the device"
                className="w-56"
              />
            ) : (
              item.deviceName
            )
          ) : (
            <>
              {cat?.name ?? priced?.name ?? item.code}
              <div className="text-xs text-muted-foreground">
                {formatMoney(cat?.price ?? priced?.unitPrice ?? 0)}
                {cat?.monthly ? "/mo" : " each"}
                {priced?.inBundle ? " · included in the bundle" : ""}
              </div>
            </>
          )}
        </TableCell>
        <TableCell className="text-right">
          {editable && maxQty > 1 ? (
            <QtyStepper value={item.qty} max={maxQty} onChange={(q) => setItem(index, { qty: q })} />
          ) : (
            <span className="tabular-nums">{item.qty}</span>
          )}
        </TableCell>
        <TableCell className="text-right">
          {own ? (
            <span className="text-muted-foreground">—</span>
          ) : editable ? (
            <CoverBox
              label={`You cover on ${cat?.name ?? item.code}`}
              percent={item.coverPercent}
              value={item.coverValue}
              disabled={lockedCover || priced?.inBundle}
              each
              onChange={(p, v) => setItem(index, { coverPercent: p, coverValue: v })}
            />
          ) : priced && priced.agentCovers > 0 ? (
            <span className="tabular-nums text-emerald-700">{formatMoney(priced.agentCovers)}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </TableCell>
        <TableCell className="text-right tabular-nums">
          {own ? "—" : priced ? `${formatMoney(priced.merchantPays)}${priced.monthly ? "/mo" : ""}` : "…"}
          {editable && priced && coveredNote(priced)}
        </TableCell>
        {editable && (
          <TableCell className="text-right">
            <Button variant="link" size="sm" className="h-auto p-0 text-muted-foreground" onClick={() => setItem(index, { qty: 0 })}>
              Remove
            </Button>
          </TableCell>
        )}
      </TableRow>
    );
  }

  function autoRow(l: EquipmentPricedLine) {
    const c = draft!.covers[l.code ?? ""] ?? { percent: false, value: 0 };
    return (
      <TableRow key={`auto-${l.code ?? l.name}`}>
        <TableCell>
          {l.name}
          <div className="text-xs text-muted-foreground">
            {formatMoney(l.unitPrice)}
            {l.kind === "License" ? " / device" : ""}
            {l.monthly ? " / month" : ""}
          </div>
        </TableCell>
        <TableCell className="text-right tabular-nums">{l.qty}</TableCell>
        <TableCell className="text-right">
          {editable ? (
            <CoverBox
              label={`You cover on ${l.name}`}
              percent={c.percent}
              value={c.value}
              each={l.qty > 1}
              onChange={(p, v) => update({ covers: { ...draft!.covers, [l.code ?? ""]: { percent: p, value: v } } })}
            />
          ) : l.agentCovers > 0 ? (
            <span className="tabular-nums text-emerald-700">{formatMoney(l.agentCovers)}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </TableCell>
        <TableCell className="text-right tabular-nums">
          {formatMoney(l.merchantPays)}
          {l.monthly ? "/mo" : ""}
          {editable && coveredNote(l)}
        </TableCell>
        {editable && <TableCell />}
      </TableRow>
    );
  }

  function setItem(index: number, patch: Partial<EquipmentLineInput>) {
    const items = draft!.items.map((it, i) => (i === index ? { ...it, ...patch } : it));
    update({ items: items.filter((it) => it.qty > 0) });
  }

  function addItem(code: string) {
    const existing = draft!.items.findIndex((i) => i.code === code);
    if (existing >= 0) {
      const max = byCode.get(code)?.maxQty ?? (byCode.get(code)?.kind === "Module" ? 1 : MAX_QTY);
      setItem(existing, { qty: Math.min(max, draft!.items[existing].qty + 1) });
      return;
    }
    update({ items: [...draft!.items, { code, deviceName: null, qty: 1, coverPercent: false, coverValue: 0 }] });
  }

  async function pickPackage(name: string, lines: [string, number][]) {
    const hardware = draft!.items.filter((i) => i.code && byCode.get(i.code)?.kind !== "Module");
    if (hardware.length > 0) {
      const ok = await confirm({
        title: `Use the ${name} package?`,
        description: "This replaces the items in the table. Modules stay.",
        confirmLabel: "Replace items",
      });
      if (!ok) return;
    }
    const modules = draft!.items.filter((i) => i.code && byCode.get(i.code)?.kind === "Module");
    setBuildOwn(false);
    update({
      items: [
        ...lines.filter(([code]) => byCode.has(code)).map(([code, qty]) => ({ code, deviceName: null, qty, coverPercent: false, coverValue: 0 })),
        ...modules,
      ],
    });
  }

  async function buildMyOwn() {
    const hardware = draft!.items.filter((i) => i.code && byCode.get(i.code)?.kind !== "Module");
    if (hardware.length > 0 && activePackage !== "own") {
      const ok = await confirm({
        title: "Build your own list?",
        description: "This empties the items table so you can add items one by one. Modules stay.",
        confirmLabel: "Empty the table",
      });
      if (!ok) return;
    }
    setBuildOwn(true);
    if (activePackage !== "own")
      update({ items: draft!.items.filter((i) => i.code && byCode.get(i.code)?.kind === "Module") });
  }

  function toggleNeed(n: EquipmentNeed) {
    const d = draft!;
    if (n === "NoneNeeded") {
      if (d.need === "NoneNeeded") return;
      update({ need: "NoneNeeded", needs: ["NoneNeeded"], items: [], covers: {}, payment: "Upfront", bundleCode: "" });
      return;
    }
    const current = d.needs.filter((x) => x !== "NoneNeeded");
    const needs = current.includes(n) ? current.filter((x) => x !== n) : [...current, n];
    if (needs.length === 0) return;
    const kindOf = (i: EquipmentLineInput) => (i.code ? byCode.get(i.code)?.kind : "OwnDevice");
    let items = d.items.filter((i) => {
      const kind = kindOf(i);
      if (kind === "Hardware" || kind === "Setup") return needs.includes("NewEquipment");
      if (kind === "OwnDevice") return needs.includes("OwnHardware");
      return true;
    });
    if (needs.includes("OwnHardware") && !items.some((i) => i.code === null))
      items = [{ code: null, deviceName: catalog!.ownDeviceModels[0] ?? "", qty: 1, coverPercent: false, coverValue: 0 }, ...items];
    update({
      need: primaryNeed(needs),
      needs,
      items,
      ...(needs.includes("NewEquipment") ? {} : { payment: "Upfront" as EquipmentPaymentOption }),
    });
  }

  async function submit() {
    setSubmitting(true);
    try {
      const saved = await saveNow(draft!);
      if (!saved) return;
      const done = await equipment.submit(id);
      dirty.current = false;
      setOrder(done);
      setDraft(toDraft(done));
      toast.success(
        done.status === "AwaitingApproval"
          ? `${done.number} submitted. An admin needs to approve it before it ships.`
          : `${done.number} submitted. The application's equipment box is ticked.`,
      );
    } catch (e) {
      toastApiError(e, "Couldn't submit the order.");
    } finally {
      setSubmitting(false);
    }
  }

  async function changeStatus(status: EquipmentOrderStatus) {
    if (status === "Draft") {
      const ok = await confirm({
        title: "Reopen this order?",
        description: "It goes back to a draft so the agent can change it and submit it again.",
        confirmLabel: "Reopen",
      });
      if (!ok) return;
    }
    if (status === "Cancelled") {
      const ok = await confirm({
        title: editable ? "Discard this draft?" : "Cancel this order?",
        description: editable ? "The draft is removed from the list." : "The order stays on record as cancelled.",
        confirmLabel: editable ? "Discard draft" : "Cancel order",
        destructive: true,
      });
      if (!ok) return;
    }
    try {
      const saved = await equipment.setStatus(id, status);
      if (status === "Cancelled" && editable) {
        navigate("/equipment");
        return;
      }
      setOrder(saved);
      setDraft(toDraft(saved));
      dirty.current = false;
      toast.success(`Status changed to ${EQUIPMENT_STATUS_LABELS[status]}.`);
    } catch (e) {
      toastApiError(e, "Couldn't change the status.");
    }
  }

  const needOptions: { value: EquipmentNeed; label: string; hint: string }[] = [
    { value: "NewEquipment", label: "New equipment", hint: "Fill the table with a package below, then adjust." },
    { value: "OwnHardware", label: "Their own hardware", hint: "They keep their devices. We reprogram them." },
    { value: "VirtualTerminal", label: "Virtual terminal only", hint: "They key cards on a computer. No hardware ships." },
    { value: "NoneNeeded", label: "No equipment needed", hint: "Nothing to order or ship, for example a second application for online ordering." },
  ];
  const paymentHint: Record<EquipmentPaymentOption, string> = {
    Upfront: "Pays for the hardware before it ships.",
    LeaseToOwn: `BLogic lends the hardware cost. Paid over ${catalog.fees.leaseTermMonths} months.`,
    FreeHardware: "You cover all the hardware. Paid back from your residuals.",
  };
  const shipHint: Record<EquipmentShipTo, string> = {
    BusinessAddress: `Ships to ${order.businessAddress ?? "the business address on the application"}. The merchant pays shipping.`,
    PickUpAtHq: "Picked up at BLogic HQ.",
    OtherAddress: "Type the address below. The merchant pays shipping.",
  };
  const closedFlag = order.applicationClosed && EQUIPMENT_FULFILLMENT_STATUSES.includes(order.status);
  const statusChoices = [order.status, ...order.nextStatuses.filter((st) => st !== order.status)];

  const placeholderFee =
    (draft.needs.includes("VirtualTerminal") && !catalog.feesDecided.virtualTerminalMonthlyFee) ||
    (draft.needs.includes("OwnHardware") && !catalog.feesDecided.ownHardwareSetupFee);

  return (
    <>
      <div className="mb-2">
        <Link to="/equipment" className="text-sm text-muted-foreground hover:underline">← Order Equipment</Link>
      </div>
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">
            {editable ? "New equipment order" : `Equipment order ${order.number}`}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <span>{order.number}</span>
            <StatusPill value={order.status} map={EQUIPMENT_STATUS_TONES} labels={EQUIPMENT_STATUS_LABELS} />
            {editable && <span>{saving ? "Saving…" : saveError ? "" : "✓ Saved automatically as a draft"}</span>}
          </div>
        </div>
        {backOffice && !editable && statusChoices.length > 1 && (
          <Select value={order.status} onValueChange={(v) => v !== order.status && void changeStatus(v as EquipmentOrderStatus)}>
            <SelectTrigger className="h-9 w-60">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {statusChoices.map((st) => (
                <SelectItem key={st} value={st}>
                  {st === order.status ? EQUIPMENT_STATUS_LABELS[st] : NEXT_STATUS_LABELS[st] ?? EQUIPMENT_STATUS_LABELS[st]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {backOffice && order.status === "AwaitingApproval" && !order.viewerIsAdmin && (
          <span className="text-sm text-amber-800">An admin needs to approve this order.</span>
        )}
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm">
        <span className="text-lg" aria-hidden>🔗</span>
        <div className="flex-1">
          <div className="font-medium text-slate-900">
            Linked to{" "}
            <Link to={`/applications/v2/${order.applicationId}`} className="text-brand-600 hover:underline">
              {order.applicationDba || "this application"}
            </Link>
          </div>
          <div className="text-xs text-muted-foreground">
            {order.agentName ? `Agent: ${order.agentName} · ` : ""}
            {order.businessAddress ? `Business address: ${order.businessAddress}` : "No business address on the application yet"}
          </div>
        </div>
        {order.applicationStatus && (
          <StatusPill value={order.applicationStatus} map={APPLICATION_STATUS_TONES} labels={APPLICATION_STATUS_LABELS} />
        )}
      </div>

      {closedFlag && (
        <Notice tone="error" className="mb-4">
          <span className="font-semibold">⚠ Don't ship this order.</span> The application is{" "}
          {APPLICATION_STATUS_LABELS[order.applicationStatus ?? ""] ?? order.applicationStatus}. Check with the fulfillment team
          before anything goes out, then cancel the order if it isn't needed.
        </Notice>
      )}
      {saveError && <Notice tone="error" className="mb-4">{saveError}</Notice>}

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          {editable && (
            <Card>
              <CardHeader className="border-b py-3">
                <CardTitle className="text-base font-semibold text-slate-900">Setup</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-5">
                <SetupRow
                  label="Needs"
                  hint={
                    draft.needs.length > 1
                      ? "Pick as many as apply."
                      : needOptions.find((n) => n.value === draft.need)?.hint
                  }
                >
                  <MultiPills
                    label="What the merchant needs"
                    values={draft.needs}
                    onToggle={toggleNeed}
                    options={needOptions.map((n) => ({ value: n.value, label: n.label }))}
                  />
                </SetupRow>
                {draft.need === "NewEquipment" && (
                  <>
                    <SetupRow label="Merchant pays" hint={paymentHint[draft.payment]}>
                      <Pills
                        label="How the merchant pays"
                        value={draft.payment}
                        onChange={(payment) => update({ payment })}
                        options={[
                          { value: "Upfront", label: "Upfront" },
                          { value: "LeaseToOwn", label: `Lease ${catalog.fees.leaseTermMonths} mo` },
                          { value: "FreeHardware", label: "Free hardware" },
                        ]}
                      />
                    </SetupRow>
                    <SetupRow label="Ship to" hint={shipHint[draft.shipTo]}>
                      <Pills
                        label="Ship to"
                        value={draft.shipTo}
                        onChange={(shipTo) => update({ shipTo })}
                        options={[
                          { value: "BusinessAddress", label: "Business address" },
                          { value: "PickUpAtHq", label: "HQ pickup" },
                          { value: "OtherAddress", label: "Other" },
                        ]}
                      />
                      {draft.shipTo === "OtherAddress" && (
                        <Input
                          className="mt-2 h-9"
                          aria-label="Shipping address"
                          placeholder="Street, city, state, ZIP"
                          value={draft.shipAddress}
                          onChange={(e) => update({ shipAddress: e.target.value })}
                        />
                      )}
                    </SetupRow>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {placeholderFee && (
            <Notice tone="warn">
              {draft.needs.includes("VirtualTerminal") && draft.needs.includes("OwnHardware")
                ? "The virtual terminal and reprogramming fees haven't"
                : draft.needs.includes("VirtualTerminal")
                  ? "The virtual terminal fee hasn't"
                  : "The reprogramming fee for merchant-owned devices hasn't"}{" "}
              been decided yet, so it isn't charged on this order.
            </Notice>
          )}

          {draft.need !== "NoneNeeded" && (
          <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 border-b py-3">
              <CardTitle className="text-base font-semibold text-slate-900">
                {draft.need === "OwnHardware" ? "Devices they're keeping, and extras" : draft.need === "VirtualTerminal" ? "Extras" : "Items"}
              </CardTitle>
              {editable && (
                <div className="flex flex-wrap items-center gap-2">
                  {draft.needs.includes("OwnHardware") && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        update({ items: [...draft.items, { code: null, deviceName: catalog.ownDeviceModels[0] ?? "", qty: 1, coverPercent: false, coverValue: 0 }] })
                      }
                    >
                      + Add a device
                    </Button>
                  )}
                  <div className="w-64">
                    <SearchableSelect
                      value=""
                      onValueChange={(v) => v && addItem(v)}
                      placeholder="+ Add an item…"
                      searchPlaceholder="Search the price list"
                      options={addable.map((i) => ({
                        value: i.code,
                        label: i.name,
                        hint: `${formatMoney(i.price)}${i.monthly ? "/mo" : ""} · ${i.category}`,
                        keywords: i.code,
                      }))}
                    />
                  </div>
                </div>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {editable && draft.need === "NewEquipment" && (
                <div className="border-b px-4 py-2.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium text-muted-foreground">Package:</span>
                    {PACKAGES.map((p) => (
                      <PackagePill key={p.id} on={activePackage === p.id} onClick={() => void pickPackage(p.name, p.lines)}>
                        {p.name}
                      </PackagePill>
                    ))}
                    <PackagePill on={activePackage === "own"} dashed onClick={() => void buildMyOwn()}>
                      Build my own
                    </PackagePill>
                  </div>
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    {matchedPackage
                      ? `${matchedPackage.hint}. Change anything below.`
                      : activePackage === "own"
                        ? "Add items with “+ Add an item”."
                        : "Pick a package to fill the list, then adjust. Or build your own."}
                  </p>
                </div>
              )}
              {visibleItems.length === 0 && autoLines.length === 0 ? (
                <p className="p-6 text-sm text-muted-foreground">
                  {draft.need === "NewEquipment" ? "Pick a package above, or add items one by one." : "Nothing added."}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">You cover</TableHead>
                      <TableHead className="text-right">Merchant pays</TableHead>
                      {editable && <TableHead />}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(oneTimeItems.length > 0 || oneTimeAuto.length > 0) && sectionRow("One-time")}
                    {oneTimeItems.map(itemRow)}
                    {oneTimeAuto.map(autoRow)}
                    {(monthlyItems.length > 0 || monthlyAuto.length > 0) && sectionRow("Monthly")}
                    {monthlyItems.map(itemRow)}
                    {monthlyAuto.map(autoRow)}
                  </TableBody>
                </Table>
              )}
              {editable && draft.need === "NewEquipment" && (
                <div className="flex flex-wrap items-center gap-3 border-t px-4 py-3">
                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Monthly bundle</span>
                  <Select value={draft.bundleCode || "none"} onValueChange={(v) => update({ bundleCode: v === "none" ? "" : v })}>
                    <SelectTrigger className="h-8 w-auto min-w-64">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No bundle (choose modules one by one)</SelectItem>
                      {bundles.map((b) => (
                        <SelectItem key={b.code} value={b.code}>
                          {b.name} · {formatMoney(b.price)}/mo · {b.note}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-xs text-muted-foreground">Billed once per location. Replaces the modules it includes.</span>
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {editable && (
            <Card>
              <CardHeader className="border-b py-3">
                <CardTitle className="text-base font-semibold text-slate-900">Notes for back office</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <Textarea rows={3} value={draft.notes} maxLength={1000} onChange={(e) => update({ notes: e.target.value })} />
              </CardContent>
            </Card>
          )}

          {!editable && (order.notes || order.shipAddress || order.need === "NewEquipment") && (
            <Card>
              <CardHeader className="border-b py-3">
                <CardTitle className="text-base font-semibold text-slate-900">Details</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {order.need === "NewEquipment" && (
                  <>
                    <SumRow
                      label="Payment"
                      value={order.payment === "Upfront" ? "Pay upfront" : order.payment === "LeaseToOwn" ? "Lease to own" : "Free hardware"}
                    />
                    <SumRow
                      label="Ship to"
                      value={
                        order.shipTo === "PickUpAtHq"
                          ? "Pick up at BLogic HQ"
                          : order.shipTo === "OtherAddress"
                            ? order.shipAddress ?? ""
                            : order.businessAddress ?? "Business address"
                      }
                    />
                  </>
                )}
                {order.notes && <SumRow label="Notes" value={order.notes} />}
                {order.submittedAt && <SumRow label="Submitted" value={formatDate(order.submittedAt)} />}
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-4 lg:sticky lg:top-4">
          <Card>
            <CardHeader className="border-b py-3">
              <CardTitle className="text-base font-semibold text-slate-900">Merchant pays</CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <SumRow label="Due at install" value={formatMoney(t.merchantDueAtInstall)} strong />
              {t.leaseMonthly > 0 && <SumRow label={`Lease, ${catalog.fees.leaseTermMonths} months`} value={`${formatMoney(t.leaseMonthly)}/mo`} />}
              <SumRow label="Monthly" value={`${formatMoney(t.merchantMonthly)}/mo`} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="border-b py-3">
              <CardTitle className="text-base font-semibold text-slate-900">You cover</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-4">
              <div>
                <SumRow label="One-time" value={formatMoney(t.agentCoversOneTime)} />
                <SumRow
                  label="Monthly"
                  value={`${formatMoney(t.agentCoversMonthly)}/mo`}
                  hint={t.agentCoversMonthly > 0 ? "Taken from your residuals each month" : undefined}
                />
              </div>
              {t.agentCoversOneTime > 0 && (
                <div>
                  <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Pay back from your residuals over
                  </span>
                  <Pills
                    label="Pay back over"
                    value={editable ? draft.paybackMonths : order.paybackMonths}
                    disabled={!editable}
                    onChange={(m) => update({ paybackMonths: m })}
                    options={catalog.paybackOptions.map((m) => ({ value: m, label: `${m} months` }))}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">Starts when the merchant goes Live.</p>
                </div>
              )}
              {editable && (
                <>
                  {order.problem && !saving && <p className="text-sm text-amber-800">{order.problem}</p>}
                  <Button className="w-full" disabled={submitting || saving || !!order.problem} onClick={() => void submit()}>
                    {draft.need === "NewEquipment" || draft.needs.length > 1
                      ? "Submit order"
                      : draft.need === "OwnHardware"
                        ? "Submit: reusing own hardware"
                        : draft.need === "VirtualTerminal"
                          ? "Submit: virtual terminal only"
                          : "Submit: no equipment needed"}
                  </Button>
                  <Button variant="ghost" className="w-full text-red-700" onClick={() => void changeStatus("Cancelled")}>
                    Discard draft
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {backOffice && order.reviewFlags && order.reviewFlags.length > 0 && (
            <Card>
              <CardHeader className="border-b py-3">
                <CardTitle className="text-base font-semibold text-slate-900">Covering rules (back office only)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 pt-4 text-sm">
                {order.reviewFlags.map((f) => (
                  <Notice key={f} tone={f.startsWith("Needs admin approval") ? "error" : "warn"}>{f}</Notice>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}

function SetupRow({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="grid items-start gap-1.5 sm:grid-cols-[8rem_minmax(0,1fr)] sm:gap-3">
      <span className="pt-1 text-sm font-medium text-slate-900">{label}</span>
      <div>
        {children}
        {hint && <p className="mt-1.5 text-xs text-muted-foreground">{hint}</p>}
      </div>
    </div>
  );
}

function PackagePill({ on, dashed, onClick, children }: { on: boolean; dashed?: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1 text-xs transition-colors",
        dashed && "border-dashed",
        on ? "border-slate-900 bg-slate-100 font-medium text-slate-900" : "text-slate-700 hover:border-slate-400 hover:bg-slate-50",
      )}
    >
      {children}
    </button>
  );
}
